import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn, isWeekendOrHoliday } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertCircle,
  ArrowRightLeft,
  CalendarIcon,
  Info,
  Plus,
} from 'lucide-react';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import type { CommandLineForm } from './CommandLineView';

interface OperationStepProps {
  form: UseFormReturn<CommandLineForm>;
}

export function OperationStep({ form }: OperationStepProps) {
  const orderNature = form.watch('orderNature');
  const mobileLineType = form.watch('mobileLineType');

  // Reset portability fields when switching to creation
  useEffect(() => {
    if (orderNature === 'creation') {
      form.setValue('portedNumber', '');
      form.setValue('rioNumber', '');
      form.setValue('physiqueOption', 'order');
      form.setValue('portabilityDate', undefined);
      form.trigger();
    }
  }, [orderNature, form]);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-slate-900">
        Nature de l'opération
      </h3>

      {mobileLineType === 'dataonly' && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            La portabilité est impossible pour les forfaits data uniquement.
          </p>
        </div>
      )}

      {/* Creation vs Portability */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => form.setValue('orderNature', 'creation')}
          className={cn(
            'rounded-xl border p-4 text-left cursor-pointer transition-all',
            orderNature === 'creation'
              ? 'border-emerald-500 bg-emerald-50'
              : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
          )}
        >
          <Plus className="h-5 w-5 text-slate-400 mb-2" />
          <p className="text-sm font-semibold text-slate-900">Nouvelle ligne</p>
          <p className="text-xs text-slate-500 mt-1">
            Nouveau numéro attribué
          </p>
        </button>

        <button
          type="button"
          onClick={() => {
            if (mobileLineType !== 'dataonly') {
              form.setValue('orderNature', 'portability');
            }
          }}
          disabled={mobileLineType === 'dataonly'}
          className={cn(
            'rounded-xl border p-4 text-left cursor-pointer transition-all',
            orderNature === 'portability'
              ? 'border-emerald-500 bg-emerald-50'
              : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5',
            mobileLineType === 'dataonly' && 'opacity-50 cursor-not-allowed'
          )}
        >
          <ArrowRightLeft className="h-5 w-5 text-slate-400 mb-2" />
          <p className="text-sm font-semibold text-slate-900">Portabilité</p>
          <p className="text-xs text-slate-500 mt-1">
            Conserver un numéro existant
          </p>
        </button>
      </div>

      {/* Portability fields */}
      {orderNature === 'portability' && (
        <div className="space-y-4 pt-2">
          <FormField
            control={form.control}
            name="portedNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-slate-700">
                  N° de ligne à conserver
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="06 12 34 56 78"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rioNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-slate-700">
                  N° RIO
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1.5 inline-flex cursor-pointer">
                          <Info className="h-3.5 w-3.5 text-slate-400" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-sm">
                        <p className="font-medium mb-1">
                          Relevé d'Identité Opérateur
                        </p>
                        <p className="text-xs">
                          Le RIO est unique et lié à une ligne mobile. Il est
                          nécessaire pour effectuer une portabilité.
                        </p>
                        <p className="text-xs mt-1">
                          <strong>Forfait particulier :</strong> Contactez le
                          3179
                        </p>
                        <p className="text-xs">
                          <strong>Forfait entreprise :</strong> Contactez votre
                          gestionnaire de flotte
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Entrez le numéro RIO"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="portabilityDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="text-sm font-medium text-slate-700">
                  Date de portabilité
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1.5 inline-flex cursor-pointer">
                          <Info className="h-3.5 w-3.5 text-slate-400" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-sm">
                        <p className="font-medium mb-1">
                          Délai minimum de 15 jours
                        </p>
                        <p className="text-xs">
                          La date doit être au minimum 15 jours après
                          aujourd'hui. Les weekends et jours fériés ne sont pas
                          disponibles.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'PPP', { locale: fr })
                        ) : (
                          <span>Sélectionner une date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 text-slate-400" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={
                        field.value ? new Date(field.value) : undefined
                      }
                      onSelect={(date) => {
                        field.onChange(date);
                        const popoverTrigger = document.querySelector(
                          '[data-state="open"]'
                        );
                        if (popoverTrigger) {
                          (popoverTrigger as HTMLElement).click();
                        }
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const minDate = new Date(today);
                        minDate.setDate(today.getDate() + 15);
                        if (date < minDate) return true;
                        return isWeekendOrHoliday(date);
                      }}
                      initialFocus
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}
